import React, { useRef } from 'react';
import Button from '@mui/joy/Button';
import ToastMessage from '../../../../../../common/ToastMessage';
import { useDropzone } from 'react-dropzone';
import { FieldErrors } from '../../../../../../domain/helpers/Errors';
import { useDarkModeStore } from '../../../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../../../store/useLanguageStore';
import { ReactComponent as UploadIcon } from '../../../../../../images/upload-folder.svg';
import { QuotationFileInterface } from '../../../../../../models/ProcurementModule/QuotationFile';
import { QuotationRequestInterface } from '../../../../../../models/ProcurementModule/QuotationRequest';

interface DropZoneCompProps {
    errors: FieldErrors | undefined;
    form: QuotationRequestInterface;
    files: QuotationFileInterface[];
    fileNames: {
        fileUrl: string;
        fileName: string;
    }[];
    setFileNames: React.Dispatch<React.SetStateAction<{
        fileUrl: string;
        fileName: string;
    }[]>>;
    setForm: React.Dispatch<React.SetStateAction<QuotationRequestInterface>>;
    setFiles: React.Dispatch<React.SetStateAction<QuotationFileInterface[]>>;

}

export const allowedFileExtension = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/pdf': ['.pdf'],
};

const maxSize = 1 * 1024 * 1024; // 1MB limit

export const DropZoneComp: React.FC<DropZoneCompProps> = ({ errors, form, files, fileNames, setForm, setFiles, setFileNames }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const { getRootProps, getInputProps } = useDropzone({
        accept: allowedFileExtension,
        onDrop: (acceptedFiles) => {
            handleFileUpload(acceptedFiles);
        },
    });

    const handleFileUpload = (uploadedFiles: File[]) => {
        if (!uploadedFiles) return;

        delete errors?.['uploadFiles'];
        delete errors?.['files'];

        const validFiles: File[] = [];
        let validationErrors = false;

        uploadedFiles.forEach((file) => {
            const fileExists = files.some((existingFile) => existingFile.fileName === file.name);
            if (fileExists) {
                ToastMessage.Warn(`The file "${file.name}" already exists.`);
                return;
            }

            if (file.size > maxSize) {
                ToastMessage.Warn(`The file "${file.name}" exceeds the 1MB size limit.`);
                return;
            }

            if (validationErrors && validFiles.length === 0) return;

            const newFile: QuotationFileInterface = {
                id: `New-File-${Math.random().toString()}`,
                fileUrl: '', // This will be updated after upload
                quotationRequestId: form.id,
                fileName: file.name,
                fileSize: file.size,
                date: file.lastModified,
                extension: file.name.split('.').pop()?.toLowerCase() ?? '',
                progress: 0,
            };

            setFiles((prevFiles) => [newFile, ...prevFiles]);
            uploadFile(file, newFile);

        });
    };

    const uploadFile = (file: File, fileWithProgress: QuotationFileInterface) => {
        let progress = 0;

        const reader = new FileReader();

        reader.onload = () => {
            const base64FileUrl = reader.result as string;

            const interval = setInterval(() => {
                const upFileNames = fileNames;
                if (progress >= 100) {
                    clearInterval(interval);

                    setFiles((prevFiles) =>
                        prevFiles.map((f) =>
                            f.id === fileWithProgress.id
                                ? { ...f, fileUrl: base64FileUrl, progress: 100 }
                                : f
                        )
                    );
                    upFileNames.push({ fileUrl: base64FileUrl, fileName: file.name });

                    setFileNames(upFileNames);
                    setForm((prevForm) => ({
                        ...prevForm,
                        files: [...(prevForm.files || []), { ...fileWithProgress, fileUrl: base64FileUrl }],
                        uploadFiles: [...(prevForm.uploadFiles || []), base64FileUrl]
                    }));

                } else {
                    progress += 10;
                    setFiles((prevFiles) =>
                        prevFiles.map((f) =>
                            f.id === fileWithProgress.id ? { ...f, progress } : f
                        )
                    );
                }
            }, 200);
        };

        reader.onerror = () => {
            console.error("Error reading file as Base64");
        };

        reader.readAsDataURL(file);
    };


    return (
        <div
            {...getRootProps()}
            className="w-50"
            style={{ border: '2px dashed #ccc', padding: '100px', cursor: 'pointer', borderRadius: 10 }}
        >
            <input {...getInputProps()} />
            <div className="d-flex flex-column align-items-center justify-content-center gap-3">
                <span style={{ color: '#DCDCDC' }}>
                    <UploadIcon width="60" height="60" />
                </span>
                <div
                    className={`d-flex flex-column fw-bold text-center ${darkMode ? 'text-white' : 'text-muted'}`}
                    style={{ fontSize: 18 }}
                >
                    <span style={{ color: 'var(--color-pink)' }}>{language['DragAndDropText']}</span>
                    <span style={{ color: 'var(--color-pink)' }}>- {language['Or'].toUpperCase()} - </span>
                </div>
                <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="font-open-sans"
                    sx={{
                        fontSize: '16px',
                        background: 'var(--btn-color)',
                        color: 'white',
                        '&:hover': {
                            background: 'var(--btn-color)',
                            opacity: 0.7,
                        },
                    }}
                >
                    {language['BrowseFiles']}
                </Button>
            </div>
        </div>
    );
};

export default DropZoneComp;
